import React, { useState, useEffect } from 'react';
import { adminApi } from './api';
import type { OverviewStats } from './types';

const StatCard: React.FC<{ label: string; value: number }> = ({ label, value }) => (
  <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
    <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">{label}</p>
    <p className="text-2xl font-bold text-gray-800 dark:text-gray-100">{value.toLocaleString()}</p>
  </div>
);

const Overview: React.FC = () => {
  const [stats, setStats] = useState<OverviewStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    adminApi.getOverviewStats()
      .then(setStats)
      .catch(e => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-gray-500 dark:text-gray-400">載入中...</div>;
  if (error) return <div className="text-red-500">載入失敗：{error}</div>;
  if (!stats) return null;

  const totalTokens = stats.tokenUsage.totalInput + stats.tokenUsage.totalOutput + stats.tokenUsage.totalThought;
  const maxDailyCount = Math.max(...stats.dailyActivity.map(d => d.count), 1);

  return (
    <div>
      <h2 className="text-xl font-bold mb-5 text-gray-800 dark:text-gray-100">概覽</h2>

      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <StatCard label="生成記錄" value={stats.totalGenerationRecords} />
        <StatCard label="AI 呼叫次數" value={stats.totalCallLogs} />
        <StatCard label="使用者數" value={stats.totalAccounts} />
        <StatCard label="總 Token 用量" value={totalTokens} />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Records by type */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-700 dark:text-gray-200 mb-4">依類型的生成記錄</h3>
          <div className="space-y-3">
            {stats.recordsByType.map(r => (
              <div key={r._id} className="flex items-center gap-3 text-sm">
                <span className="text-gray-600 dark:text-gray-400 w-32 shrink-0 truncate">{r._id}</span>
                <div className="flex-1 bg-gray-100 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-500 h-2 rounded-full"
                    style={{ width: `${stats.totalGenerationRecords ? (r.count / stats.totalGenerationRecords) * 100 : 0}%` }}
                  />
                </div>
                <span className="text-gray-800 dark:text-gray-200 font-medium w-8 text-right shrink-0">{r.count}</span>
              </div>
            ))}
            {stats.recordsByType.length === 0 && <p className="text-sm text-gray-400">無資料</p>}
          </div>
        </div>

        {/* Call logs by model */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-700 dark:text-gray-200 mb-4">AI 模型使用統計</h3>
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs text-gray-500 dark:text-gray-400 border-b dark:border-gray-700">
                <th className="text-left pb-2">模型</th>
                <th className="text-right pb-2">次數</th>
                <th className="text-right pb-2">輸入</th>
                <th className="text-right pb-2">輸出</th>
              </tr>
            </thead>
            <tbody>
              {stats.callLogsByModel.map(m => (
                <tr key={m._id} className="border-b dark:border-gray-700 last:border-0 text-gray-700 dark:text-gray-300">
                  <td className="py-2 text-xs max-w-28 truncate">{m._id}</td>
                  <td className="py-2 text-right">{m.count}</td>
                  <td className="py-2 text-right">{m.inputTokens.toLocaleString()}</td>
                  <td className="py-2 text-right">{m.outputTokens.toLocaleString()}</td>
                </tr>
              ))}
              {stats.callLogsByModel.length === 0 && (
                <tr><td colSpan={4} className="py-4 text-center text-gray-400 text-xs">無資料</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Token usage detail */}
      <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700 mb-6">
        <h3 className="font-semibold text-gray-700 dark:text-gray-200 mb-4">Token 使用明細</h3>
        <div className="grid grid-cols-3 gap-4 text-center">
          {[
            { label: '輸入 Token', value: stats.tokenUsage.totalInput },
            { label: '輸出 Token', value: stats.tokenUsage.totalOutput },
            { label: '思考 Token', value: stats.tokenUsage.totalThought },
          ].map(item => (
            <div key={item.label}>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-1">{item.label}</p>
              <p className="text-xl font-bold text-gray-800 dark:text-gray-100">{item.value.toLocaleString()}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Daily activity bar chart */}
      {stats.dailyActivity.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-xl p-5 shadow-sm border border-gray-100 dark:border-gray-700">
          <h3 className="font-semibold text-gray-700 dark:text-gray-200 mb-4">每日生成記錄（近 30 天）</h3>
          <div className="flex items-end gap-1 h-24">
            {stats.dailyActivity.map(d => (
              <div
                key={d._id}
                className="flex-1 bg-blue-400 dark:bg-blue-600 rounded-t hover:bg-blue-600 dark:hover:bg-blue-400 transition-colors cursor-default min-w-0"
                style={{ height: `${(d.count / maxDailyCount) * 88}px` }}
                title={`${d._id}: ${d.count} 筆`}
              />
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-400 mt-2">
            <span>{stats.dailyActivity[0]?._id}</span>
            <span>{stats.dailyActivity[stats.dailyActivity.length - 1]?._id}</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default Overview;
